import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { MessagesSquare } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import MedienFeedbackAufnahme, { MedienFeedbackAbspieler } from '@/components/MedienFeedbackAufnahme';

/* Coach-Chat (13.10.2026, gesamtabgleich A5): ein Verlauf je Mitglied zwischen Mitglied (App) und Team (CRM und /coach).
   Beim Öffnen gilt der Verlauf als gelesen. Aufrufe als Props (eigene Anmeldung auf /coach).
   W2.6 (29.10.2026), freiwillige Props: `medien` ({ hochladen(fd, cfg), datei(fid) }) zeigt den Aufnahme-Knopf für
   Video-/Audio-Feedback und spielt Feedbacks im Verlauf ab („angesehen am …“); `audio(nid)` spielt Sprachnachrichten
   (auch die des Mitglieds aus der App). */

function SprachNachricht({ nid, audio }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    audio(nid).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => setUrl(null));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [nid, audio]);
  return url ? <audio controls src={url} className="mb-1 h-9 w-full max-w-xs" /> : <span className="block text-xs opacity-80">Sprachnachricht wird geladen …</span>;
}

const zeit = (iso) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

export default function CoachChat({ laden, senden, gelesen, testId = 'coach-chat', leadId, medien, audio }) {
  const [d, setD] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const ende = useRef(null);
  const lade = useCallback(() => laden().then((r) => setD(r.data)).catch(() => setD(null)), [laden]);
  useEffect(() => { lade().then(() => gelesen().catch(() => {})); }, [lade, gelesen]);
  useEffect(() => { ende.current?.scrollIntoView?.({ block: 'nearest' }); }, [d]);
  if (!d) return <Skeleton className="h-20" />;
  const schicken = async () => {
    setBusy(true);
    try { await senden(text); setText(''); await lade(); } catch (e) { toast.error(fehlerText(e, 'Nicht gesendet')); }
    setBusy(false);
  };
  return (
    <Card className="border-border/60" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><MessagesSquare className="h-4 w-4 text-primary" /> Chat</CardTitle>
        <p className="text-xs text-muted-foreground">{d.offen ? 'Das Mitglied schreibt in der App zurück (Push ohne Inhalt).' : 'Das Mitglied kann erst antworten, wenn ihr schreibt oder es betreut wird (Coaching, Ernährung, PT, Probe-Plan).'}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
          {!d.nachrichten.length && <p className="text-sm text-muted-foreground">Noch keine Nachrichten.</p>}
          {d.nachrichten.map((m) => (
            <div key={m.id} className={`flex ${m.von === 'coach' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-[12px] px-3 py-2 text-sm break-words whitespace-pre-line ${m.von === 'coach' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                <span className={`block text-[10px] ${m.von === 'coach' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>{m.name || (m.von === 'coach' ? 'Team' : 'Mitglied')} · {zeit(m.am)}</span>
                {m.audio && audio && <SprachNachricht nid={m.id} audio={audio} />}
                {m.medien && medien?.datei && <div className="mb-1"><MedienFeedbackAbspieler medien={m.medien} datei={medien.datei} hell={m.von === 'coach'} /></div>}
                {m.text}
              </div>
            </div>
          ))}
          <div ref={ende} />
        </div>
        <div className="flex items-end gap-2">
          <label className="sr-only" htmlFor={`${testId}-text`}>Nachricht</label>
          <Textarea id={`${testId}-text`} rows={1} maxLength={2000} className="min-h-[44px] text-base sm:text-sm" placeholder="Nachricht …" value={text} onChange={(e) => setText(e.target.value)} />
          {medien?.hochladen && <MedienFeedbackAufnahme leadId={leadId} bezug="chat" client={medien} kompakt disabled={busy} onGesendet={() => lade()} />}
          <Button className="h-11" disabled={busy || !text.trim()} onClick={schicken}>Senden</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function CoachChatCrm({ leadId }) {
  const f = useMemo(() => ({
    laden: () => api.get(`/coach-chat/${leadId}`),
    senden: (text) => api.post(`/coach-chat/${leadId}`, { text }),
    gelesen: () => api.post(`/coach-chat/${leadId}/gelesen`),
    audio: (nid) => api.get(`/coach-chat/${leadId}/${nid}/audio`, { responseType: 'blob', timeout: 60000 }),
    medien: {
      hochladen: (fd, cfg = {}) => api.post(`/medien-feedback/mitglied/${leadId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000, ...cfg }),
      datei: (fid) => api.get(`/medien-feedback/mitglied/${leadId}/${fid}/datei`, { responseType: 'blob', timeout: 180000 }),
    },
  }), [leadId]);
  return <CoachChat key={leadId} leadId={leadId} {...f} />;
}
